import React, { useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { t } from '@konrad/reweb-aem';

import { SelectInput, TextInput } from '../_shared';
import { Button, Icon, Text } from '../index';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import {
    CURRENCY_MASK_OPTIONS,
    MORTGAGE_PRE_PAYMENT_CALCULATOR,
    PERCENTAGE_MASK_OPTIONS,
    MONTHS_NAMES,
} from '../../lib/constants';
import { getHelpTextId, removeSymbols } from '../../lib/helpers/form';

import styles from './MortgagePrePaymentCalculator.module.scss';

const MortgagePrePaymentCalculator = (props) => {
    const {
        firstStep,
        interestTitle,
        interestSubtitle,
        loanTitle,
        loanSubtitle,
        maximumTitle,
        maximumSubtitle,
        dateTitle,
        dateSubtitle,
        dateValue,
        nextStepTitle,
        amountTenderedTitle,
        amountTenderedSubtitle,
        buttonText,
        buttonAltText,
        resultTitle,
        resultSubtitle,
    } = props;

    const locale = document.documentElement.lang;
    const currencyMaskEn = createNumberMask(CURRENCY_MASK_OPTIONS.EN);
    const currencyMaskFr = createNumberMask(CURRENCY_MASK_OPTIONS.FR);
    const percentageMask = createNumberMask(PERCENTAGE_MASK_OPTIONS);
    const interestRateId = 'interest-rate';
    const interestRateHelpText = getHelpTextId(interestRateId);
    const loanId = 'loan';
    const loanHelpText = getHelpTextId(loanId);
    const maxPaymentId = 'max-payment';
    const maxPaymentHelpText = getHelpTextId(maxPaymentId);
    const dateId = 'date';
    const dateHelpText = getHelpTextId(dateId);
    const dayId = 'day';
    const dayValue = dateValue && dateValue.dayOfMonth;
    const monthId = 'month';
    const yearId = 'year';
    const yearValue = dateValue && dateValue.year;
    const amountTenderedId = 'amount-tendered';
    const amountTenderedHelpText = getHelpTextId(amountTenderedId);
    const resultId = 'result';
    const resultHelpText = getHelpTextId(resultId);
    const todayDate = new Date();
    const dateOfLoan = 'date-loan';

    const getMonths = (year) => {
        let months = [];
        let monthNames = locale === 'fr' ? MONTHS_NAMES.FR : MONTHS_NAMES.EN;
        monthNames.forEach((name, index) => {
            if (parseInt(year) === todayDate.getFullYear()) {
                if (todayDate.getMonth() >= index) {
                    months.push({ name: name, value: index });
                }
            } else {
                months.push({ name: name, value: index });
            }
        });

        return months;
    };

    const getDaysInMonth = (month, year) => {
        let date;
        if (!month || !year) {
            date = new Date(new Date().getFullYear(), 0, 1);
            month = 0;
        } else {
            date = new Date(year, month.value ? month.value : month, 1);
        }
        let days = [];
        while (date.getMonth() === month.value || date.getMonth() === month) {
            if (
                date.getMonth() === todayDate.getMonth() &&
                date.getFullYear() === todayDate.getFullYear()
            ) {
                if (date.getDate() <= todayDate.getDate()) {
                    days.push({ name: new Date(date).getDate(), value: new Date(date).getDate() });
                }
            } else {
                days.push({ name: new Date(date).getDate(), value: new Date(date).getDate() });
            }
            date.setDate(date.getDate() + 1);
        }
        return days;
    };

    const getYears = () => {
        let years = [];
        let startYear = MORTGAGE_PRE_PAYMENT_CALCULATOR.DATE.startYear;
        while (startYear <= todayDate.getFullYear()) {
            years.push({ name: startYear, value: startYear++ });
        }
        return years;
    };

    //Set custom month or use placeholder (Month)
    const initialMonth = dateValue ? getMonths()[dateValue.month].value : null;
    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    //Set custom month or use placeholder (Year)
    const initialYear = yearValue ? getYears().find((e) => e.value === yearValue).value : null;
    const [selectedYear, setSelectedYear] = useState(initialYear);

    const [result, setResult] = useState(0);

    const [input, setInput] = useState({
        [interestRateId]: '',
        [loanId]: '',
        [maxPaymentId]: 0,
        [monthId]: selectedMonth,
        [yearId]: selectedYear,
        [dayId]: dayValue,
        [amountTenderedId]: '',
    });

    const [submitAttempted, setSubmitAttempted] = useState(false);

    //Validations
    const invalidInterest =
        input[interestRateId] < MORTGAGE_PRE_PAYMENT_CALCULATOR.INTEREST_RATE.min ||
        input[interestRateId] > MORTGAGE_PRE_PAYMENT_CALCULATOR.INTEREST_RATE.max;
    const invalidLoan =
        input[loanId] < MORTGAGE_PRE_PAYMENT_CALCULATOR.LOAN.min ||
        input[loanId] > MORTGAGE_PRE_PAYMENT_CALCULATOR.LOAN.max;
    const invalidAmount = input[amountTenderedId] > input[loanId] || input[amountTenderedId] === '';
    const invalidDate = input[dayId] === '' || input[monthId] === null || !input[yearId];
    const emptyInputs =
        input[dayId] === '' ||
        input[monthId] === '' ||
        input[yearId] === '' ||
        input[amountTenderedId] === '' ||
        input[interestRateId] === '' ||
        input[loanId] === '';

    const handleInputChange = (event) => {
        const target = event.target;
        const name = target.name;
        let value = target.value;

        if (name === interestRateId) {
            value = Number(removeSymbols(value)).toFixed(2);
        }

        if (name === loanId || name === amountTenderedId) {
            value = Number(removeSymbols(value));
        }

        if (name === monthId) {
            setSelectedMonth(getMonths(selectedYear)[value].value);
        }

        if (name === yearId) {
            setSelectedYear(value);
        }

        setInput({
            ...input,
            [name]: value,
            ...(name === loanId && {
                [maxPaymentId]: parseInt((value * 100) / 1000),
            }),
            ...(name === monthId && {
                [dayId]: 1,
                [monthId]: value ? value : null,
            }),
            ...(name === yearId && {
                [yearId]: value ? value : null,
                [monthId]: getMonths(value)[selectedMonth]
                    ? getMonths(value)[selectedMonth].value
                    : null,
                [dayId]: 1,
            }),
        });
    };

    const getValidationDate = () => {
        let date = new Date();
        date.setMonth(date.getMonth() - MORTGAGE_PRE_PAYMENT_CALCULATOR.CALCULATIONS.minMonths);
        return date;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!submitAttempted) setSubmitAttempted(true);
        if (!invalidInterest && !invalidLoan && !invalidAmount && !invalidDate && !emptyInputs) {
            let date = new Date(input[yearId], input[monthId], input[dayId]);
            if (date >= getValidationDate()) {
                const amountForPenalty = input[amountTenderedId] - input[maxPaymentId];
                const rateValue = amountForPenalty * (parseFloat(input[interestRateId]) / 100);
                const penaltyAmount =
                    (rateValue / MORTGAGE_PRE_PAYMENT_CALCULATOR.CALCULATIONS.days) *
                    MORTGAGE_PRE_PAYMENT_CALCULATOR.CALCULATIONS.penaltyConstant;
                //Use 0 if penalty amount is negative
                setResult(
                    penaltyAmount < 0
                        ? 0
                        : Math.floor(penaltyAmount).toLocaleString(locale, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                          })
                );
            } else {
                setResult(0);
            }
        } else {
            setResult(0);
        }
    };

    return (
        <div className={styles.root}>
            <form className={cx('light-theme', styles.form)} onSubmit={handleSubmit}>
                <span className={styles['step']}>{firstStep}</span>
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    errorMessage={t('fs.mortgage.calculator-interest-rate-error')}
                    fieldClassName={styles['text-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={interestRateHelpText}
                    id={interestRateId}
                    isInvalid={submitAttempted && invalidInterest}
                    mask={percentageMask}
                    subtitle={interestSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={interestTitle}
                    value={input[interestRateId]}
                />
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    errorMessage={t('fs.mortgage.calculator-loan-error')}
                    fieldClassName={styles['text-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={loanHelpText}
                    id={loanId}
                    isInvalid={submitAttempted && invalidLoan}
                    mask={locale === 'fr' ? currencyMaskFr : currencyMaskEn}
                    subtitle={loanSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={loanTitle}
                    value={input[loanId]}
                />
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['text-field']}
                    helpTextId={maxPaymentHelpText}
                    id={maxPaymentId}
                    mask={locale === 'fr' ? currencyMaskFr : currencyMaskEn}
                    subtitle={maximumSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={maximumTitle}
                    value={input[maxPaymentId]}
                    disabled={true}
                />
                <label
                    className={cx(styles.title, {
                        [styles['title-error']]: submitAttempted && invalidDate,
                    })}
                    id={dateOfLoan}
                >
                    {dateTitle}
                </label>
                <div className={styles['date-container']}>
                    <SelectInput
                        containerClassName={styles['input-container--select']}
                        fieldClassName={cx(styles['select-field'], {
                            [styles['field-error']]: submitAttempted && invalidDate,
                        })}
                        handleInputChange={handleInputChange}
                        id={monthId}
                        helpTextId={dateOfLoan}
                        isInvalid={false}
                        options={getMonths(selectedYear)}
                        value={input[monthId]}
                        placeholder={t('fs.mortgage.calculator-month')}
                    />
                    <SelectInput
                        containerClassName={styles['input-container--select']}
                        fieldClassName={cx(styles['select-field'], {
                            [styles['field-error']]: submitAttempted && invalidDate,
                        })}
                        handleInputChange={handleInputChange}
                        helpTextId={dateOfLoan}
                        id={dayId}
                        isInvalid={false}
                        options={getDaysInMonth(selectedMonth, selectedYear)}
                        value={input[dayId]}
                        placeholder={t('fs.mortgage.calculator-day')}
                    />
                    <SelectInput
                        containerClassName={styles['input-container--select']}
                        fieldClassName={cx(styles['select-field'], {
                            [styles['field-error']]: submitAttempted && invalidDate,
                        })}
                        handleInputChange={handleInputChange}
                        helpTextId={dateOfLoan}
                        id={yearId}
                        isInvalid={false}
                        options={getYears()}
                        value={input[yearId]}
                        placeholder={t('fs.mortgage.calculator-year')}
                    />
                </div>
                <div className={cx(styles.subtitle, styles['date-subtitle'])} id={dateHelpText}>
                    {dateSubtitle}
                </div>
                {submitAttempted && invalidDate && (
                    <div className={styles['error-message-container']}>
                        <Icon
                            className={cx(styles['error-message-icon'])}
                            size="x-small"
                            url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/attention.svg"
                        />
                        <div className={styles['error-message-text']}>
                            {t('fs.mortgage.calculator-date-error')}
                        </div>
                    </div>
                )}
                <span className={styles['step-date']}>{nextStepTitle}</span>
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    errorMessage={t('fs.mortgage.calculator-amount-error')}
                    fieldClassName={styles['text-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={amountTenderedHelpText}
                    id={amountTenderedId}
                    isInvalid={submitAttempted && invalidAmount}
                    mask={locale === 'fr' ? currencyMaskFr : currencyMaskEn}
                    subtitle={amountTenderedSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={amountTenderedTitle}
                    value={input[amountTenderedId]}
                />
                <Button
                    altText={buttonAltText}
                    className={styles.cta}
                    linkText={buttonText}
                    type="form"
                />
                {submitAttempted && emptyInputs && (
                    <div className={styles['error-message-container']}>
                        <Icon
                            className={cx(styles['error-message-icon'])}
                            size="x-small"
                            url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/attention.svg"
                        />
                        <div className={styles['error-message-text']}>
                            {t('fs.mortgage.calculator-form-error')}
                        </div>
                    </div>
                )}
            </form>
            <div className={cx('dark-theme', styles['results-container'])}>
                <div className={styles['results-title']} id={resultId}>
                    {resultTitle}
                </div>
                <div
                    aria-describedby={resultHelpText}
                    aria-labelledby={resultId}
                    className={styles['results-payment']}
                >
                    {t('fs.mortgage.calculator-result', [result])}
                    <span className={styles['results-payment-asterisk']}>*</span>
                </div>
                <div id={resultHelpText} className={styles['results-subtitle']}>
                    <Text
                        className={styles['transcription']}
                        content={resultSubtitle}
                        size="body"
                    />
                </div>
            </div>
        </div>
    );
};

MortgagePrePaymentCalculator.displayName = 'MortgagePrePaymentCalculator';

MortgagePrePaymentCalculator.propTypes = {
    firstStep: PropTypes.string.isRequired,
    interestTitle: PropTypes.string.isRequired,
    interestSubtitle: PropTypes.string,
    loanTitle: PropTypes.string.isRequired,
    loanSubtitle: PropTypes.string,
    maximumTitle: PropTypes.string.isRequired,
    maximumSubtitle: PropTypes.string,
    dateTitle: PropTypes.string.isRequired,
    dateSubtitle: PropTypes.string,
    nextStepTitle: PropTypes.string.isRequired,
    amountTenderedTitle: PropTypes.string.isRequired,
    amountTenderedSubtitle: PropTypes.string,
    buttonText: PropTypes.string.isRequired,
    buttonAltText: PropTypes.string.isRequired,
    resultTitle: PropTypes.string.isRequired,
    resultSubtitle: PropTypes.string,
};

MortgagePrePaymentCalculator.defaultProps = {
    firstStep: t('fs.mortgage.calculator-first-step-default'),
    interestTitle: t('fs.mortgage.calculator-interest-title-default'),
    interestSubtitle: t('fs.mortgage.calculator-interest-subtitle-default'),
    loanTitle: t('fs.mortgage.calculator-loan-title-default'),
    loanSubtitle: t('fs.mortgage.calculator-loan-subtitle-default'),
    maximumTitle: t('fs.mortgage.calculator-loan-title-default'),
    maximumSubtitle: t('fs.mortgage.calculator-maximum-subtitle-default'),
    dateTitle: t('fs.mortgage.calculator-date-title-default'),
    dateSubtitle: t('fs.mortgage.calculator-date-subtitle-default'),
    nextStepTitle: t('fs.mortgage.calculator-next-step-default'),
    amountTenderedTitle: t('fs.mortgage.calculator-amount-title-default'),
    amountTenderedSubtitle: t('fs.mortgage.calculator-amount-subtitle-default'),
};

export default MortgagePrePaymentCalculator;
